import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNedarimIframe, translateNedarimError } from '../StandingOrders/NewStandingOrder/useNedarimIframe';
import styles from './PublicDonation.module.css';
import { BILLING_ENTITY_BY_CURRENCY, DonationFormState, DonationPageConfig } from './types';

interface Props {
  config: DonationPageConfig;
  form: DonationFormState;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const CreditCardStep = ({ config, form, onSuccess, onError }: Props) => {
  const { t, i18n } = useTranslation();
  const [iframeUrl] = useState(() => `https://matara.pro/nedarimplus/iframe?language=${i18n.language === 'he' ? 'he' : 'en'}`);
  const { iframeRef, iframeHeight, status, transactionResponse, handleIframeLoad } = useNedarimIframe();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (status === 'success') onSuccess();
    else if (status === 'error' && transactionResponse?.Message) {
      onError(translateNedarimError(transactionResponse.Message, t));
      setIsSubmitting(false);
    }
  }, [status, transactionResponse, onSuccess, onError]);

  const handlePay = useCallback(() => {
    if (isSubmitting) return;
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    setIsSubmitting(true);

    const fundMarker = config.isParentMosad ? `MHF-${config.fundId}` : '';
    const isStandingOrder = form.months > 1;
    iframe.contentWindow.postMessage({
      Name: 'FinishTransaction2',
      Value: {
        Mosad: String(config.mosadId),
        ApiValid: config.apiValid,
        PaymentType: isStandingOrder ? 'HK' : 'Ragil',
        Currency: form.currency === 'USD' ? '2' : '1',
        Zeout: form.currency === 'USD' ? '888888888' : form.donor.israeliId,
        FirstName: form.donor.firstName,
        LastName: form.donor.lastName,
        Street: form.donor.address,
        City: form.donor.city,
        Phone: form.donor.phone,
        Mail: form.donor.email,
        Amount: String(form.amount),
        Tashlumim: isStandingOrder ? String(form.months) : '1',
        Day: isStandingOrder ? String(form.chargeDay) : '',
        Groupe: fundMarker,
        Comment: '',
        Param1: '',
        Param2: String(config.fundId),
        CallBack: '',
        CallBackMailError: '',
        ThirdPartyReceipt: '',
      },
    }, '*');
  }, [config, form, iframeRef, isSubmitting]);

  return (
    <div className={styles.section}>
      <div className={styles.iframeSection}>
        {status === 'loading' && <div className={styles.iframeLoading}>{t('donations.loadingPayment')}</div>}
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          onLoad={handleIframeLoad}
          style={{ width: 'calc(100% + 16px)', marginInlineStart: '-8px', height: iframeHeight ? `${iframeHeight}px` : '0', border: 'none', overflow: 'hidden' }}
          title="Nedarim Plus"
        />
        {status === 'submitting' && <div className={styles.iframeLoading}>{t('donations.processing')}</div>}
      </div>
      <div className={styles.billingNoteBold}>
        {t('donations.billingNote', { entity: BILLING_ENTITY_BY_CURRENCY[form.currency] })}
      </div>
      {status === 'ready' && (
        <button type="button" className={styles.submitBtn} onClick={handlePay} disabled={isSubmitting}>
          {isSubmitting ? t('donations.processing') : t('donations.payNow')}
        </button>
      )}
    </div>
  );
};

export default CreditCardStep;
